import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Edit2, Loader2, Plus, Search, ToggleLeft, ToggleRight, Users } from "lucide-react"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Switch } from "shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  createContractorWorker,
  searchContractorCompanies,
  searchContractorWorkers,
  updateContractorWorker,
  type ContractorCompanyDto,
  type ContractorWorkerAdminDto,
} from "modules/admin/api/contractorWorkerService"

interface ContractorManagementProps {
  currentUser?: { name?: string }
}

type ContractorFormState = {
  firstName: string
  lastName: string
  companyId: string
  idType: string
  nationalId: string
  passportNo: string
  cardNumber: string
  status: string
}

const TENANT_ID = DEFAULT_TENANT_ID
const NO_COMPANY = "__none__"

const ID_TYPES = [
  { value: "NATIONAL_ID", label: "National ID" },
  { value: "PASSPORT", label: "Passport" },
]

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
]

const initialForm: ContractorFormState = {
  firstName: "",
  lastName: "",
  companyId: NO_COMPANY,
  idType: "NATIONAL_ID",
  nationalId: "",
  passportNo: "",
  cardNumber: "",
  status: "ACTIVE",
}

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toISOString().slice(0, 10)
}

export function ContractorManagement({ currentUser }: ContractorManagementProps) {
  const [items, setItems] = useState<ContractorWorkerAdminDto[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [companyFilter, setCompanyFilter] = useState(NO_COMPANY)
  const [isLoading, setIsLoading] = useState(false)

  const [companyOptions, setCompanyOptions] = useState<ContractorCompanyDto[]>([])
  const [companySearch, setCompanySearch] = useState("")
  const [isCompanyLoading, setIsCompanyLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ContractorWorkerAdminDto | null>(null)
  const [form, setForm] = useState<ContractorFormState>(initialForm)

  const isEditing = Boolean(editing)
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])

  const selectedCompanyLabel = useMemo(() => {
    if (!form.companyId || form.companyId === NO_COMPANY) return null
    const match = companyOptions.find((company) => company.companyId === form.companyId)
    if (match) return match.companyName
    if (editing?.companyId === form.companyId && editing.companyName) return editing.companyName
    return form.companyId
  }, [companyOptions, editing, form.companyId])

  const loadWorkers = useCallback(
    async (currentPage = 1) => {
      setIsLoading(true)
      try {
        const result = await searchContractorWorkers({
          tenantId: TENANT_ID,
          search: searchTerm || undefined,
          companyId: companyFilter !== NO_COMPANY ? companyFilter : undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          pageNumber: currentPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? currentPage)
      } catch (error) {
        console.error(error)
        toast.error("Failed to load contractor workers")
      } finally {
        setIsLoading(false)
      }
    },
    [companyFilter, pageSize, searchTerm, statusFilter],
  )

  const loadCompanies = useCallback(async (search?: string) => {
    setIsCompanyLoading(true)
    try {
      const result = await searchContractorCompanies({
        tenantId: TENANT_ID,
        search: search || undefined,
        pageNumber: 1,
        pageSize: 200,
      })
      setCompanyOptions(result.items ?? [])
    } catch (error) {
      console.error(error)
      toast.error("Failed to load contractor companies")
    } finally {
      setIsCompanyLoading(false)
    }
  }, [])

  const handleClearFilters = () => {
    setSearchTerm("")
    setStatusFilter("all")
    setCompanyFilter(NO_COMPANY)
    setCompanySearch("")
    loadWorkers(1)
  }

  const handlePageSizeChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return
    setPageSize(parsed)
    loadWorkers(1)
  }

  useEffect(() => {
    loadWorkers(1)
  }, [loadWorkers])

  useEffect(() => {
    loadCompanies()
  }, [loadCompanies])

  useEffect(() => {
    const handler = window.setTimeout(() => {
      loadCompanies(companySearch.trim())
    }, 300)

    return () => {
      window.clearTimeout(handler)
    }
  }, [companySearch, loadCompanies])

  const openCreate = () => {
    setEditing(null)
    setForm(initialForm)
    setCompanySearch("")
    setDialogOpen(true)
  }

  const openEdit = (item: ContractorWorkerAdminDto) => {
    setEditing(item)
    setForm({
      firstName: item.firstName ?? "",
      lastName: item.lastName ?? "",
      companyId: item.companyId ?? NO_COMPANY,
      idType: item.idType ?? "NATIONAL_ID",
      nationalId: item.nationalId ?? "",
      passportNo: item.passportNo ?? "",
      cardNumber: item.cardNumber ?? "",
      status: item.status ?? "ACTIVE",
    })
    setCompanySearch("")
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("First name and last name are required")
      return
    }

    if (!form.companyId || form.companyId === NO_COMPANY) {
      toast.error("Company is required")
      return
    }

    if (!form.idType.trim()) {
      toast.error("ID type is required")
      return
    }

    if (form.idType === "NATIONAL_ID" && !form.nationalId.trim()) {
      toast.error("National ID is required")
      return
    }

    if (form.idType === "PASSPORT" && !form.passportNo.trim()) {
      toast.error("Passport number is required")
      return
    }

    const payload = {
      tenantId: TENANT_ID,
      companyId: form.companyId,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      idType: form.idType,
      nationalId: form.idType === "NATIONAL_ID" ? form.nationalId.trim() : null,
      passportNo: form.idType === "PASSPORT" ? form.passportNo.trim() : null,
      cardNumber: form.cardNumber.trim() || null,
      status: form.status,
    }

    try {
      if (isEditing && editing) {
        await updateContractorWorker(editing.workerId, payload)
        toast.success("Updated contractor worker")
      } else {
        await createContractorWorker(payload)
        toast.success("Created contractor worker")
      }
      setDialogOpen(false)
      loadWorkers(page)
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "Failed to save contractor worker"
      toast.error(typeof message === "string" ? message : "Failed to save contractor worker")
    }
  }

  const handleToggleStatus = async (item: ContractorWorkerAdminDto) => {
    const nextStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
    try {
      await updateContractorWorker(item.workerId, {
        tenantId: TENANT_ID,
        companyId: item.companyId,
        firstName: item.firstName,
        lastName: item.lastName,
        idType: item.idType,
        nationalId: item.idType === "NATIONAL_ID" ? item.nationalId ?? null : null,
        passportNo: item.idType === "PASSPORT" ? item.passportNo ?? null : null,
        cardNumber: item.cardNumber ?? null,
        status: nextStatus,
      })
      toast.success(`Status changed to ${nextStatus}`)
      loadWorkers(page)
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "Failed to update status"
      toast.error(typeof message === "string" ? message : "Failed to update status")
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Users className="h-5 w-5 text-blue-600" />
              Contractor Management
            </CardTitle>
            <p className="text-sm text-gray-500">
              Manage contractor workers{currentUser?.name ? ` (admin: ${currentUser.name})` : ""}
            </p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> Add Worker
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                placeholder="Search name / ID / card"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && loadWorkers(1)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent className="max-h-72 p-0">
                <div className="sticky top-0 z-10 border-b bg-white p-2">
                  <Input
                    placeholder="Search company"
                    value={companySearch}
                    onChange={(event) => setCompanySearch(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                </div>
                <SelectItem value={NO_COMPANY}>All companies</SelectItem>
                {isCompanyLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                ) : (
                  companyOptions.map((company) => (
                    <SelectItem key={company.companyId} value={company.companyId}>
                      {company.companyName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadWorkers(1)} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
            </Button>
            <Button variant="ghost" onClick={handleClearFilters} disabled={isLoading}>
              Clear filters
            </Button>
            <div className="text-sm text-gray-500">Total {totalItems.toLocaleString("en-US")} items</div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Card No</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.workerId}>
                      <TableCell>
                        <div className="font-medium">{item.fullName}</div>
                        <div className="text-xs text-gray-500">{item.firstName} {item.lastName}</div>
                      </TableCell>
                      <TableCell>{item.companyName || "-"}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {item.idType === "PASSPORT" ? "Passport" : "National ID"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.idType === "PASSPORT" ? item.passportNo ?? "-" : item.nationalId ?? "-"}
                        </div>
                      </TableCell>
                      <TableCell>{item.cardNumber ?? "-"}</TableCell>
                      <TableCell>
                        <Badge variant={item.status === "ACTIVE" ? "default" : "secondary"}>
                          {item.status === "ACTIVE" ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(item.updatedAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="gap-1">
                            <Edit2 className="h-4 w-4" /> Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(item)}
                            className="gap-1"
                          >
                            {item.status === "ACTIVE" ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-500" />
                            )}
                            {item.status === "ACTIVE" ? "Disable" : "Enable"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <span>Page {page} / {totalPages}</span>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => loadWorkers(page - 1)}>
                Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => loadWorkers(page + 1)}>
                Next
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Per page</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[90px]">
                    <SelectValue placeholder="20" />
                  </SelectTrigger>
                  <SelectContent>
                    {["10", "20", "50", "100"].map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Contractor Worker" : "Add Contractor Worker"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={form.firstName}
                  onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={form.lastName}
                  onChange={(event) => setForm((prev) => ({ ...prev, lastName: event.target.value }))}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Company *</Label>
              <Select
                value={form.companyId}
                onValueChange={(value) => setForm((prev) => ({ ...prev, companyId: value }))}
              >
                <SelectTrigger>
                  {form.companyId === NO_COMPANY ? (
                    <SelectValue placeholder="Select company" />
                  ) : (
                    <span className="line-clamp-1 text-left text-sm">{selectedCompanyLabel}</span>
                  )}
                </SelectTrigger>
                <SelectContent className="max-h-72 p-0">
                  <div className="sticky top-0 z-10 border-b bg-white p-2">
                    <Input
                      placeholder="Search company"
                      value={companySearch}
                      onChange={(event) => setCompanySearch(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    />
                  </div>
                  <SelectItem value={NO_COMPANY}>None</SelectItem>
                  {isCompanyLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                  ) : (
                    companyOptions.map((company) => (
                      <SelectItem key={company.companyId} value={company.companyId}>
                        {company.companyName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>ID Type *</Label>
                <Select
                  value={form.idType}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      idType: value,
                      nationalId: value === "NATIONAL_ID" ? prev.nationalId : "",
                      passportNo: value === "PASSPORT" ? prev.passportNo : "",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ID type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ID_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{form.idType === "PASSPORT" ? "Passport No *" : "National ID *"}</Label>
                <Input
                  value={form.idType === "PASSPORT" ? form.passportNo : form.nationalId}
                  onChange={(event) =>
                    setForm((prev) =>
                      prev.idType === "PASSPORT"
                        ? { ...prev, passportNo: event.target.value }
                        : { ...prev, nationalId: event.target.value },
                    )
                  }
                  placeholder={form.idType === "PASSPORT" ? "Passport number" : "National ID"}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Card Number</Label>
                <Input
                  value={form.cardNumber}
                  onChange={(event) => setForm((prev) => ({ ...prev, cardNumber: event.target.value }))}
                  placeholder="Card number"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="contractor-active"
                checked={form.status === "ACTIVE"}
                onCheckedChange={(value) => setForm((prev) => ({ ...prev, status: value ? "ACTIVE" : "INACTIVE" }))}
              />
              <Label htmlFor="contractor-active">Active</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>{isEditing ? "Update" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ContractorManagement
